export type MapFacePoint = { x: number; y: number; z?: number };
export type ProjectedFacePoint = { x: number; y: number; depth: number };
type ScreenPoint = { x: number; y: number };

const EPSILON = 1e-7;

function cross(a: ScreenPoint, b: ScreenPoint, c: ScreenPoint) {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function contains(polygon: ScreenPoint[], point: ScreenPoint) {
  let inside = false;
  for (let index = 0; index < polygon.length; index++) {
    const a = polygon[index];
    const b = polygon[(index + 1) % polygon.length];
    if (
      Math.abs(cross(a, b, point)) < EPSILON &&
      point.x >= Math.min(a.x, b.x) - EPSILON &&
      point.x <= Math.max(a.x, b.x) + EPSILON &&
      point.y >= Math.min(a.y, b.y) - EPSILON &&
      point.y <= Math.max(a.y, b.y) + EPSILON
    )
      return true;
    if (
      a.y > point.y !== b.y > point.y &&
      point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x
    )
      inside = !inside;
  }
  return inside;
}

function intersection(
  a: ScreenPoint,
  b: ScreenPoint,
  c: ScreenPoint,
  d: ScreenPoint,
) {
  const denominator = (b.x - a.x) * (d.y - c.y) - (b.y - a.y) * (d.x - c.x);
  if (Math.abs(denominator) < EPSILON) return undefined;
  const t =
    ((c.x - a.x) * (d.y - c.y) - (c.y - a.y) * (d.x - c.x)) / denominator;
  const u =
    ((c.x - a.x) * (b.y - a.y) - (c.y - a.y) * (b.x - a.x)) / denominator;
  if (t < -EPSILON || t > 1 + EPSILON || u < -EPSILON || u > 1 + EPSILON)
    return undefined;
  return { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) };
}

function depthPlane(points: ProjectedFacePoint[]) {
  const origin = points[0];
  let determinant = 0;
  let first = origin;
  let second = origin;
  for (let index = 1; index < points.length - 1; index++) {
    const value = cross(origin, points[index], points[index + 1]);
    if (Math.abs(value) > Math.abs(determinant)) {
      determinant = value;
      first = points[index];
      second = points[index + 1];
    }
  }
  if (Math.abs(determinant) < EPSILON) return undefined;
  const dx =
    ((first.depth - origin.depth) * (second.y - origin.y) -
      (second.depth - origin.depth) * (first.y - origin.y)) /
    determinant;
  const dy =
    ((first.x - origin.x) * (second.depth - origin.depth) -
      (second.x - origin.x) * (first.depth - origin.depth)) /
    determinant;
  return (point: ScreenPoint) =>
    origin.depth + dx * (point.x - origin.x) + dy * (point.y - origin.y);
}

/**
 * Paint non-intersecting planar faces from farthest to nearest. Centroid depth
 * is only a tie breaker: a long wall can have a nearer centroid while still
 * lying behind a neighbouring roof wherever their screen projections overlap.
 */
export function orderMapFaces<T extends { points: MapFacePoint[] }>(
  faces: readonly T[],
  project: (point: MapFacePoint) => ProjectedFacePoint,
): T[] {
  const projected = faces.map((face, index) => {
    const points = face.points.map(project);
    return {
      face,
      index,
      points,
      plane: depthPlane(points),
      depth:
        points.reduce((sum, point) => sum + point.depth, 0) / points.length,
      left: Math.min(...points.map((point) => point.x)),
      right: Math.max(...points.map((point) => point.x)),
      top: Math.min(...points.map((point) => point.y)),
      bottom: Math.max(...points.map((point) => point.y)),
    };
  });
  const outgoing: number[][] = faces.map(() => []);
  const incoming = new Uint16Array(faces.length);

  for (let first = 0; first < projected.length; first++) {
    const a = projected[first];
    if (!a.plane) continue;
    for (let second = first + 1; second < projected.length; second++) {
      const b = projected[second];
      // Most stands are separated; reject those pairs before polygon work.
      if (
        !b.plane ||
        a.right <= b.left + EPSILON ||
        b.right <= a.left + EPSILON ||
        a.bottom <= b.top + EPSILON ||
        b.bottom <= a.top + EPSILON
      )
        continue;
      // The intersection vertices come from contained source vertices and edge
      // crossings. This preserves concave roofs without taking a convex hull.
      const overlap: ScreenPoint[] = [
        ...a.points.filter((point) => contains(b.points, point)),
        ...b.points.filter((point) => contains(a.points, point)),
      ];
      for (let ai = 0; ai < a.points.length; ai++) {
        for (let bi = 0; bi < b.points.length; bi++) {
          const point = intersection(
            a.points[ai],
            a.points[(ai + 1) % a.points.length],
            b.points[bi],
            b.points[(bi + 1) % b.points.length],
          );
          if (point) overlap.push(point);
        }
      }
      if (overlap.length < 3) continue;
      const start = overlap[0];
      const next = overlap.find(
        (point) => Math.hypot(point.x - start.x, point.y - start.y) > EPSILON,
      );
      if (
        !next ||
        !overlap.some((point) => Math.abs(cross(start, next, point)) > EPSILON)
      )
        continue;

      let minimum = Infinity;
      let maximum = -Infinity;
      for (const point of overlap) {
        const difference = a.plane(point) - b.plane(point);
        minimum = Math.min(minimum, difference);
        maximum = Math.max(maximum, difference);
      }
      // A plane's depth is affine, so the overlap vertices bound its difference
      // everywhere inside the overlap, including each part of a concave roof.
      let far: number;
      let near: number;
      if (minimum >= -EPSILON && maximum > EPSILON) {
        far = second;
        near = first;
      } else if (maximum <= EPSILON && minimum < -EPSILON) {
        far = first;
        near = second;
      } else {
        // Coplanar faces need no ordering constraint. Intersecting faces cannot
        // have a whole-face painter order; venue stands never interpenetrate.
        continue;
      }
      outgoing[far].push(near);
      incoming[near]++;
    }
  }

  const remaining = projected.sort(
    (a, b) => a.depth - b.depth || a.index - b.index,
  );
  const ordered: T[] = [];
  while (remaining.length) {
    const available = remaining.findIndex((face) => incoming[face.index] === 0);
    // Keep a deterministic result if a future geometry snapshot introduces a
    // painter cycle. Current disjoint venue solids have an acyclic order.
    const [next] = remaining.splice(available < 0 ? 0 : available, 1);
    ordered.push(next.face);
    for (const after of outgoing[next.index]) incoming[after]--;
  }
  return ordered;
}
